import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { BiMenu, BiX } from "react-icons/bi";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import {
  hideLoginModal,
  hideRegisterModal,
  showLoginModal,
  showRegisterModal,
} from "../../store/auth/login/actions";
import { useDispatch } from "react-redux";
import { logout as logoutThunk } from "../../store/auth/actions";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import AuthContainer from "../../screen/Auth";
import { clearLastRoute } from "../../store/routes/actions";
import VendorOrbisLogo from "../../assets/vendorOrbis.png";
import { BiRightArrowAlt } from "react-icons/bi";
import WorkspaceSwitcher from "../WorkspaceSwitcher";
import { useWorkspaces } from "../../hooks/useWorkspaces";
import { defaultDashboardPath } from "../../screen/Settings/defaultDashboard";
import { canUseEventDiscovery } from "../../screen/EventDiscovery/eventDiscoveryAccess";

interface AvatarProps {
  $isActive?: boolean;
  $isVisible?: boolean;
}

const NavWrapper = styled.nav<{ borderless?: boolean; $dark?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50; /* Ensures the navbar stays on top */
  background: ${({ $dark }) => ($dark ? "#070b17" : "white")};
  border-bottom: 1px solid
    ${({ $dark }) => ($dark ? "rgba(255, 255, 255, 0.06)" : "transparent")};

  padding: 0.75rem 1.5rem;
  display: flex;
  justify-content: center;
  transition: background-color 0.3s ease-in-out;
`;

const NavInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1280px; /* or 1140px, 1024px depending on your layout */
  gap: 1rem;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const Logo = styled(RouterLink)`
  display: flex;
  align-items: center;
  cursor: pointer;
  text-decoration: none;

  img {
    height: 36px;
    width: auto;
    object-fit: contain;
  }
`;

const NavLink = styled.span<{ active?: boolean; $dark?: boolean }>`
  font-weight: 500;
  color: ${({ active, $dark }) =>
    $dark ? (active ? "#ffffff" : "#cbd5e1") : active ? "#111827" : "#6b7280"};
  font-size: 0.95rem;
  cursor: pointer;
  position: relative;
  padding: 0.25rem 0;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -4px;
    height: 2px;
    width: 100%;
    background-color: ${({ active }) =>
      active ? "#2563eb" : "transparent"}; /* changed here */
    transition: background-color 0.2s ease-in-out;
  }

  &:hover {
    color: ${({ $dark }) => ($dark ? "#ffffff" : "#1f2937")};
  }
`;

const AppNavLink = styled(RouterLink)<{ $active?: boolean }>`
  font-weight: 500;
  color: ${({ $active }) => ($active ? "#111827" : "#6b7280")};
  font-size: 0.95rem;
  cursor: pointer;
  position: relative;
  padding: 0.25rem 0;
  text-decoration: none;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -4px;
    height: 2px;
    width: 100%;
    background-color: ${({ $active }) =>
      $active ? "#2563eb" : "transparent"};
    transition: background-color 0.2s ease-in-out;
  }

  &:hover {
    color: #1f2937;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const RightNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
`;

const DesktopOnly = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;
  align-items: center;
  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenuButton = styled.button<{ $dark?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 1px solid
    ${({ $dark }) => ($dark ? "rgba(255, 255, 255, 0.15)" : "#e5e7eb")};
  background: ${({ $dark }) => ($dark ? "rgba(255, 255, 255, 0.06)" : "#ffffff")};
  color: ${({ $dark }) => ($dark ? "#ffffff" : "#111827")};
  cursor: pointer;
`;

const MobileMenuBackdrop = styled.button<{ $open: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: ${({ $open }) => ($open ? "block" : "none")};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
    border: none;
    z-index: 40;
  }
`;

const MobileMenu = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: block;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 60;
    background: #ffffff;
    border-top: 1px solid #e5e7eb;
    padding: 0.75rem 1rem 1rem;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
    transform: ${({ $open }) => ($open ? "translateY(0)" : "translateY(-6px)")};
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }
`;

const MobileMenuSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.25rem 0;
`;

const MobileMenuLink = styled.button<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  border: 1px solid ${({ $primary }) => ($primary ? "#2563eb" : "#e5e7eb")};
  background: ${({ $primary }) => ($primary ? "#2563eb" : "#ffffff")};
  color: ${({ $primary }) => ($primary ? "#ffffff" : "#111827")};
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
`;

const MobileDivider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 0.5rem 0;
`;

const ActionText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: white;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-left: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const TextActionButton = styled.button<{ $dark?: boolean }>`
  border: none;
  background: transparent;
  padding: 0;
  color: ${({ $dark }) => ($dark ? "#cbd5e1" : "#64748b")};
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ $dark }) => ($dark ? "#ffffff" : "#111827")};
  }
`;

const RegisterButton = styled.button`
  border: none;
  background: #2563eb;
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.7rem 1.3rem;
  border-radius: 9999px;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background: #1d4ed8;
    transform: translateY(-1px);
  }
`;

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector((state: RootState) => state.auth.token);
  const isAuthenticated = !!token;
  const user = useSelector((state: RootState) => state.auth.user);
  // const user = getUser();
  const isActive = (path: string) => location.pathname === path;
  const isPublicMarketingPage =
    location.pathname === "/" ||
    location.pathname === "/faq" ||
    location.pathname === "/support" ||
    location.pathname.startsWith("/tools");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const isLoginModalVisible = useSelector(
    (state: any) => state.login.isLoginModalVisible,
  );
  const isRegisterModalVisible = useSelector(
    (state: RootState) => state.login.isRegisterModalVisible,
  );
  const canSeeAdminAnalytics =
    String(user?.email || "").toLowerCase() === "beelgulzar@gmail.com";
  const canSeeDiscover = canUseEventDiscovery(user);
  const {
    workspaces,
    active: activeWorkspace,
    switchTo: switchWorkspace,
  } = useWorkspaces(isAuthenticated);
  // Working in an employer's business: employees only handle events.
  const isEmployeeView = activeWorkspace?.role === "employee";
  // The Settings choice is the owner's; employees can't open every page.
  const dashboardPath = isEmployeeView ? "/events" : defaultDashboardPath();
  const attemptedRoute = useRef(location.pathname);

  // Pages with a dark hero get a dark navbar.
  const isDarkNav =
    location.pathname === "/" ||
    location.pathname === "/pricing" ||
    location.pathname === "/faq";

  useEffect(() => {
    const handleLoginModalVisibility = () => {
      const protectedRoutes = ["/my-uploads", "/profile"];
      if (protectedRoutes.includes(location.pathname)) {
        if (!isAuthenticated) {
          attemptedRoute.current = location.pathname;
          dispatch(showLoginModal());
        } else {
          dispatch(hideLoginModal());
        }
      }
    };

    handleLoginModalVisibility();
  }, [location, isAuthenticated, dispatch]);

  // A page that asked for sign-in (e.g. /import) wins over the current route.
  const lastRoute = useSelector((state: RootState) => state.lastRoute.lastRoute);
  useEffect(() => {
    if (isAuthenticated && isLoginModalVisible) {
      navigate(lastRoute || attemptedRoute.current);
      if (lastRoute) dispatch(clearLastRoute());
      dispatch(hideLoginModal());
    }
  }, [isAuthenticated, isLoginModalVisible, lastRoute, navigate, dispatch]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`); // fallback for future SSR or if not rendered
    }
  };

  return (
    <>
      <NavWrapper
        borderless={location.pathname === "/"}
        $dark={isDarkNav}
      >
        <NavInner>
          <LeftSection>
            <Logo to="/">
              <img src={VendorOrbisLogo} alt="VendorOrbis Logo" />
            </Logo>

            <DesktopOnly>
              {!isPublicMarketingPage &&
              isAuthenticated &&
                location.pathname !== "/pricing" ? (
                <>
                  <AppNavLink
                    to="/events"
                    $active={location.pathname.startsWith("/events")}
                  >
                    Events
                  </AppNavLink>
                  {canSeeDiscover && (
                    <AppNavLink
                      to="/discover"
                      $active={location.pathname.startsWith("/discover")}
                    >
                      Discover
                    </AppNavLink>
                  )}
                  {!isEmployeeView && (
                    <>
                      <AppNavLink
                        to="/inventory"
                        $active={isActive("/inventory")}
                      >
                        Inventory
                      </AppNavLink>
                      <AppNavLink to="/finance" $active={isActive("/finance")}>
                        Finance
                      </AppNavLink>
                      <AppNavLink
                        to="/tools"
                        $active={
                          location.pathname.startsWith("/tools") ||
                          location.pathname.startsWith("/import") ||
                          location.pathname.startsWith("/live-auctions") ||
                          location.pathname.startsWith("/pdf-editor")
                        }
                      >
                        Tools
                      </AppNavLink>
                    </>
                  )}
                  {canSeeAdminAnalytics && (
                    <AppNavLink
                      to="/admin/analytics"
                      $active={isActive("/admin/analytics")}
                    >
                      Analytics
                    </AppNavLink>
                  )}
                </>
              ) : null}
            </DesktopOnly>
          </LeftSection>

          <RightSection>
            <DesktopOnly>
              <ButtonContainer ref={dropdownRef}>
                {location.pathname === "/pricing" ? (
                  !isAuthenticated ? (
                    // Show "Sign In ->" for logged-out users
                    <ActionText
                      onClick={() => {
                        dispatch(hideRegisterModal());
                        dispatch(showLoginModal());
                      }}
                    >
                      Sign In <BiRightArrowAlt size={20} />
                    </ActionText>
                  ) : (
                    // Show "Dashboard ->" for logged-in users
                    <ActionText onClick={() => navigate(dashboardPath)}>
                      Dashboard <BiRightArrowAlt size={20} />
                    </ActionText>
                  )
                ) : !isAuthenticated ? (
                  <CSSTransition
                    in
                    timeout={300}
                    classNames="fade"
                    unmountOnExit
                  >
                    <RightNavGroup>
                      {isPublicMarketingPage && (
                        <>
                          <NavLink $dark={isDarkNav} onClick={() => scrollToSection("features")}>
                            Features
                          </NavLink>
                          <NavLink $dark={isDarkNav} onClick={() => navigate("/pricing")}>
                            Pricing
                          </NavLink>
                          <NavLink $dark={isDarkNav} onClick={() => navigate("/faq")}>
                            Support
                          </NavLink>
                          <NavLink $dark={isDarkNav} onClick={() => navigate("/tools")}>
                            Tools
                          </NavLink>
                        </>
                      )}
                      <TextActionButton
                        $dark={isDarkNav}
                        type="button"
                        onClick={() => {
                          dispatch(hideRegisterModal());
                          dispatch(showLoginModal());
                        }}
                      >
                        Log in
                      </TextActionButton>
                      <RegisterButton
                        type="button"
                        onClick={() => {
                          dispatch(hideLoginModal());
                          dispatch(showRegisterModal());
                        }}
                      >
                        Register
                      </RegisterButton>
                    </RightNavGroup>
                  </CSSTransition>
                ) : (
                  <>
                    {isPublicMarketingPage && (
                      <RightNavGroup>
                        <NavLink $dark={isDarkNav} onClick={() => scrollToSection("features")}>
                          Features
                        </NavLink>
                        <NavLink $dark={isDarkNav} onClick={() => navigate("/pricing")}>
                          Pricing
                        </NavLink>
                        <NavLink $dark={isDarkNav} onClick={() => navigate("/faq")}>
                          Support
                        </NavLink>
                        <NavLink $dark={isDarkNav} onClick={() => navigate("/tools")}>
                          Tools
                        </NavLink>
                        <DashboardButton onClick={() => navigate(dashboardPath)}>
                          Go to Dashboard
                        </DashboardButton>
                      </RightNavGroup>
                    )}

                    {!isPublicMarketingPage && (
                      <>
                        <SwitcherSlot>
                          <WorkspaceSwitcher
                            workspaces={workspaces}
                            active={activeWorkspace}
                            onSwitch={switchWorkspace}
                          />
                        </SwitcherSlot>
                        <CSSTransition
                          in
                          timeout={300}
                          classNames="fade"
                          unmountOnExit
                        >
                          <Avatar
                            src={
                              user?.user_avatar ||
                              `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(
                                user?.email || "Guest",
                              )}`
                            }
                            alt="Avatar"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            $isActive={isDropdownOpen}
                            $isVisible={true}
                          />
                        </CSSTransition>

                        <DropdownMenu isOpen={isDropdownOpen}>
                          <DropdownItem
                            style={{ fontWeight: 500 }}
                            onClick={() => {
                              setIsDropdownOpen(false);
                              navigate("/settings");
                            }}
                          >
                            Settings
                          </DropdownItem>
                          <DropdownItem
                            style={{ fontWeight: 500 }}
                            onClick={() => {
                              setIsDropdownOpen(false);
                              navigate("/billing");
                            }}
                          >
                            Billing
                          </DropdownItem>
                          {canSeeAdminAnalytics && (
                            <DropdownItem
                              style={{ fontWeight: 500 }}
                              onClick={() => {
                                setIsDropdownOpen(false);
                                navigate("/admin/analytics");
                              }}
                            >
                              Analytics
                            </DropdownItem>
                          )}
                          <DropdownItem
                            style={{ color: "#ef4444", fontWeight: 500 }}
                            onClick={() => {
                              dispatch(logoutThunk() as any);
                              setIsDropdownOpen(false);
                              navigate("/");
                            }}
                          >
                            Sign Out
                          </DropdownItem>
                        </DropdownMenu>
                      </>
                    )}
                  </>
                )}
              </ButtonContainer>
            </DesktopOnly>

            <MobileOnly>
              <MobileMenuButton
                $dark={isDarkNav}
                type="button"
                aria-label="Toggle menu"
                onClick={() => setIsMobileMenuOpen((v) => !v)}
              >
                {isMobileMenuOpen ? <BiX size={22} /> : <BiMenu size={22} />}
              </MobileMenuButton>
            </MobileOnly>
          </RightSection>
        </NavInner>

        <MobileMenuBackdrop
          $open={isMobileMenuOpen}
          aria-label="Close menu"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        <MobileMenu $open={isMobileMenuOpen}>
          <MobileMenuSection>
            {isPublicMarketingPage ? (
              <>
                <MobileMenuLink onClick={() => scrollToSection("features")}>
                  Features
                </MobileMenuLink>
                <MobileMenuLink onClick={() => navigate("/pricing")}>
                  Pricing
                </MobileMenuLink>
                <MobileMenuLink onClick={() => navigate("/faq")}>
                  Support
                </MobileMenuLink>
                <MobileMenuLink onClick={() => navigate("/tools")}>
                  Tools
                </MobileMenuLink>
              </>
            ) : isAuthenticated && location.pathname !== "/pricing" ? (
              <>
                <WorkspaceSwitcher
                  workspaces={workspaces}
                  active={activeWorkspace}
                  onSwitch={switchWorkspace}
                  fullWidth
                />
                <MobileMenuLink onClick={() => navigate("/events")}>
                  Events
                </MobileMenuLink>
                {canSeeDiscover && (
                  <MobileMenuLink onClick={() => navigate("/discover")}>
                    Discover
                  </MobileMenuLink>
                )}
                {!isEmployeeView && (
                  <>
                    <MobileMenuLink onClick={() => navigate("/inventory")}>
                      Inventory
                    </MobileMenuLink>
                    <MobileMenuLink onClick={() => navigate("/finance")}>
                      Finance
                    </MobileMenuLink>
                    <MobileMenuLink onClick={() => navigate("/tools")}>
                      Tools
                    </MobileMenuLink>
                  </>
                )}
                {canSeeAdminAnalytics && (
                  <MobileMenuLink onClick={() => navigate("/admin/analytics")}>
                    Analytics
                  </MobileMenuLink>
                )}

              </>
            ) : null}
          </MobileMenuSection>

          <MobileDivider />

          <MobileMenuSection>
            {location.pathname === "/pricing" ? (
              !isAuthenticated ? (
                <MobileMenuLink
                  $primary
                  onClick={() => {
                    dispatch(hideRegisterModal());
                    dispatch(showLoginModal());
                  }}
                >
                  Sign In
                </MobileMenuLink>
              ) : (
                <MobileMenuLink $primary onClick={() => navigate(dashboardPath)}>
                  Dashboard
                </MobileMenuLink>
              )
            ) : !isAuthenticated ? (
              <>
                <MobileMenuLink
                  onClick={() => {
                    dispatch(hideRegisterModal());
                    dispatch(showLoginModal());
                  }}
                >
                  Log in
                </MobileMenuLink>
                <MobileMenuLink
                  $primary
                  onClick={() => {
                    dispatch(hideLoginModal());
                    dispatch(showRegisterModal());
                  }}
                >
                  Register
                </MobileMenuLink>
              </>
            ) : (
              <>
                {isPublicMarketingPage && (
                  <MobileMenuLink
                    $primary
                    onClick={() => navigate(dashboardPath)}
                  >
                    Go to Dashboard
                  </MobileMenuLink>
                )}
                {!isPublicMarketingPage && (
                  <>
                    <MobileMenuLink onClick={() => navigate("/settings")}>
                      Settings
                    </MobileMenuLink>
                    <MobileMenuLink onClick={() => navigate("/billing")}>
                      Billing
                    </MobileMenuLink>
                    {canSeeAdminAnalytics && (
                      <MobileMenuLink
                        onClick={() => navigate("/admin/analytics")}
                      >
                        Analytics
                      </MobileMenuLink>
                    )}
                    <MobileMenuLink
                      onClick={() => {
                        dispatch(logoutThunk() as any);
                        navigate("/");
                      }}
                    >
                      Sign Out
                    </MobileMenuLink>
                  </>
                )}
              </>
            )}
          </MobileMenuSection>
        </MobileMenu>
      </NavWrapper>
      {(isLoginModalVisible || isRegisterModalVisible) && (
        <AuthContainer
          onClose={() => {
            dispatch(hideLoginModal());
            dispatch(hideRegisterModal());
            dispatch(clearLastRoute());
          }}
        />
      )}
    </>
  );
};

export default NavBar;

const Avatar = styled.img<AvatarProps>`
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  object-fit: cover;
  cursor: pointer;

  border: ${({ $isActive }) =>
    $isActive
      ? "2px solid #2563eb"
      : "2px solid transparent"}; /* changed here */

  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transform: ${({ $isVisible, $isActive }) =>
    $isVisible ? ($isActive ? "scale(1.15)" : "scale(1)") : "scale(0.5)"};
  transition:
    opacity 0.3s,
    transform 0.3s,
    border 0.3s;

  position: relative;
  z-index: 2;

  &:hover {
    transform: scale(1.15);
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 55px;
  right: 0;
  width: 180px;
  background-color: #ffffff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 0.5rem 0;
  z-index: 100;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transform: translateY(${({ isOpen }) => (isOpen ? "0" : "-10px")});
  pointer-events: ${({ isOpen }) => (isOpen ? "auto" : "none")};
  transition: all 0.2s ease-in-out;
`;
const SwitcherSlot = styled.div`
  margin-right: 0.75rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  height: 50px;
  width: auto; /* changed from fixed 90px */
`;

const DropdownItem = styled.div`
  padding: 0.5rem 1rem;
  color: #374151;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover {
    background-color: #f3f4f6;
  }
`;
const DashboardButton = styled.button`
  background-color: #ffffff;
  color: #2563eb;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0.5rem 1.25rem;
  border: 0.1px solid #e5e7eb; /* subtle light gray border */
  border-radius: 8px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  /* Soft elevated shadow */
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);

  &:hover {
    background-color: #f9fafb;
  }
`;
